/**
 * 输入栏纯逻辑（任务 10d）：键盘意图 / autosize 钳制 / 软键盘 inset 计算。
 * Composer.tsx 只做 DOM 接线，口径全在这里（可单测）。
 *
 * - 回车口径（PROMPT 定稿）：PC=Enter 发送、Shift+Enter 换行；H5=Enter 换行、
 *   点按钮发送。「回车发送可切换」设置项归 14.31，本轮为默认行为。
 * - IME：isComposing（含 Safari keyCode 229 期间）一律不发送——中文输入法
 *   选词回车只上屏不发消息。
 * - 软键盘 inset：iOS Safari / 安卓 Chrome 默认键盘是**覆盖**visual viewport
 *   而不缩 layout viewport，inset = 聊天页底边 − 可视视口底边，作为聊天页
 *   padding-bottom 把输入栏顶到键盘上方；键盘收起自然归 0。地址栏伸缩等小幅
 *   差值（<40px）忽略，防止非键盘场景顶起布局。
 *   **两边都量 client 坐标**（21ah-3）：页面底边取 `getBoundingClientRect().bottom`
 *   而不是 `document.body.clientHeight`/`innerHeight`——Web Clip 下键盘弹出时
 *   iOS 会把整页往上滚一截（页头都滚出屏幕），用「高度」当基准就会把那一截
 *   重复计一遍，输入栏顶过头、与键盘之间空出一条白带（人工指图实拍）。rect
 *   是现测的，页被滚了自然就小了，不用猜滚了多少。
 */

import { attachmentKindFor } from '~/chat/fileSend';
import { isMediaBlocked, type ComposerRestriction } from '~/chat/groupRestrictionModel';
import { MsgType } from '~/chat/msgTypes';

export interface ComposerKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly isComposing: boolean;
}

/**
 * 键盘按下意图：'send'=拦截默认行为并发送；'none'=放行（换行/普通输入）。
 *
 * **不分端**（21am-5，人工报「H5 回车怎么成换行了」）：Enter 发送、Shift+Enter
 * 换行、IME 组字期间一律不发，H5 与 PC 同一套。
 *
 * 原先按布局断点 `isMobile`（视口 <768px，layoutEnv）短路成恒换行，与旧版行为
 * 不一致——旧版的判据是 `PlatformCapabilities.supportsKeyboardShortcuts`
 * = `isPcRuntime` = `kIsWeb || isDesktopNative`（`utils/platform_capabilities.dart`），
 * **`kIsWeb` 对任何 web 构建都为真、不看屏幕宽度**，所以旧网页版在 H5 宽度下
 * 回车同样是发送。按宽度分端还有个副作用：PC 上把窗口拖窄到 768px 以下，
 * 物理键盘的回车就突然不发送了。
 *
 * 真手机软键盘下这意味着换行要靠 Shift+Enter（旧版网页版同样如此）；
 * 需要「回车发送」开关的话归 14.31，本函数只负责默认行为。
 */
export function composerKeyIntent(e: ComposerKeyEvent): 'send' | 'none' {
  if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return 'none';
  return 'send';
}

/** autosize 上限（约 5 行）；低于单行高度由浏览器最小内容高度兜底 */
export const COMPOSER_MAX_HEIGHT = 120;

export function autosizeHeight(scrollHeight: number): number {
  return Math.min(scrollHeight, COMPOSER_MAX_HEIGHT);
}

/** 非键盘场景的 visual viewport 小幅抖动（iOS 地址栏/滚动条）忽略阈值 */
const KEYBOARD_MIN_INSET = 40;

/**
 * 键盘占用高度 = 聊天页底边 − 可视视口底边（都在 layout viewport 的 client
 * 坐标系里量，见文件头 21ah-3）。
 *
 * - `pageBottom`：聊天页根元素 `getBoundingClientRect().bottom`；
 * - 可视视口底边 = `vv.offsetTop + vv.height`。
 *
 * 负数（页面比可视视口还矮）钳 0；<40px 的小幅差值当地址栏抖动忽略。
 */
export function keyboardInset(
  pageBottom: number,
  vvHeight: number,
  vvOffsetTop: number,
): number {
  const inset = Math.round(pageBottom - vvHeight - vvOffsetTop);
  return inset >= KEYBOARD_MIN_INSET ? inset : 0;
}

/** 发送前整理：去首尾空白；全空白不发 */
export function normalizeDraftText(raw: string): string {
  return raw.trim();
}

/**
 * 从文件选择/粘贴/拖拽中筛出图片（12b）。按 MIME 前缀过滤——file input 的
 * accept 只是建议（拖拽/粘贴不走它），这里是真正的门禁。
 */
export function pickImageFiles(list: ArrayLike<File> | null | undefined): File[] {
  if (!list) return [];
  return Array.from(list).filter((f) => f.type.startsWith('image/'));
}

/** 图片发送拒绝/失败 → 用户提示文案（'ok' 返回空串不提示） */
export function imageSendErrorText(outcome: string): string {
  switch (outcome) {
    case 'ok':
      return '';
    case 'too-large':
      return '图片超过 5MB，无法发送';
    case 'empty':
      return '无法读取该图片';
    default:
      return '图片发送失败，请重试';
  }
}

/** 文件/视频发送拒绝/失败 → 用户提示文案（14.3b；5MB=uploadv2 全类型统一上限） */
export function fileSendErrorText(outcome: string, isVideo: boolean): string {
  const noun = isVideo ? '视频' : '文件';
  switch (outcome) {
    case 'ok':
      return '';
    case 'too-large':
      return `${noun}超过 5MB，无法发送`;
    case 'empty':
      return `无法读取该${noun}`;
    default:
      return `${noun}发送失败，请重试`;
  }
}

/**
 * 群媒体限制拦截提示（14.15b）。图片位 2 沿用旧版文案（旧版只拦图片位，贴纸 44
 * 归并同位——groupRestrictionModel）；4/7/8 是新版分位扩展（对齐后端
 * IsMessageAllowed），文案按同款句式补齐。整栏禁言另走「禁言中」占位/toast。
 */
export function blockedMediaText(msgType: number): string {
  switch (msgType) {
    case 2:
      return '群主已禁止发送图片和表情';
    case 4:
      return '群主已禁止发送语音';
    case 7:
      return '群主已禁止发送视频';
    case 8:
      return '群主已禁止发送文件';
    default:
      return '群主已禁止发送此类消息';
  }
}

/**
 * 附件路径（附件按钮/PC 拖拽）按限制掩码分位过滤（14.15b）：分流口径与
 * useImageSender 一致（fileSend.attachmentKindFor：图片→2 / 视频→7 / 其余→8），
 * 被禁的剔除并报被禁类型（去重，调用方取首个出提示），放行的继续发送——
 * 混投时不整批拦死（服务端对漏网的兜底拒发）。
 */
export function filterBlockedAttachments<T extends { name: string; type: string }>(
  files: readonly T[],
  restriction: Pick<ComposerRestriction, 'blockedMedia'>,
): { allowed: T[]; blockedTypes: number[] } {
  const allowed: T[] = [];
  const blockedTypes: number[] = [];
  for (const f of files) {
    const kind = attachmentKindFor(f.name, f.type);
    const msgType = kind === 'image' ? MsgType.Image : kind === 'video' ? MsgType.Video : MsgType.File;
    if (isMediaBlocked(restriction, msgType)) {
      if (!blockedTypes.includes(msgType)) blockedTypes.push(msgType);
    } else {
      allowed.push(f);
    }
  }
  return { allowed, blockedTypes };
}
